import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SessionAuth } from 'supertokens-auth-react/recipe/session';
import MockupDisplayCard from './MockupDisplayCard';
import { api, getUserMockups, normalizePaginatedItems, MOCKUPS_PAGE_SIZE } from '../apiClient';

const MockupsPage = () => {
  const [mockups, setMockups] = useState([]);
  const [mockupsTotal, setMockupsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMockupIds, setSelectedMockupIds] = useState(new Set()); // Use a Set for efficient lookups
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMockups = async () => {
      try {
        const data = await getUserMockups({ limit: MOCKUPS_PAGE_SIZE, offset: 0 });
        const rows = normalizePaginatedItems(data);
        setMockups(rows);
        setMockupsTotal(typeof data?.total === 'number' ? data.total : rows.length);
      } catch (err) {
        setError('Failed to fetch mockups.');
        console.error('Error fetching mockups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMockups();
  }, []);

  const loadMoreMockups = async () => {
    if (loadingMore || mockups.length >= mockupsTotal) return;
    try {
      setLoadingMore(true);
      const data = await getUserMockups({ limit: MOCKUPS_PAGE_SIZE, offset: mockups.length });
      const rows = normalizePaginatedItems(data);
      if (typeof data?.total === 'number') setMockupsTotal(data.total);
      setMockups((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const merged = [...prev];
        for (const m of rows) {
          if (m && m.id != null && !seen.has(m.id)) {
            seen.add(m.id);
            merged.push(m);
          }
        }
        return merged;
      });
    } catch (err) {
      console.error('Error loading more mockups:', err);
      setError('Failed to load more mockups.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSelectMockup = (mockupId) => {
    setSelectedMockupIds((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(mockupId)) {
        newSelected.delete(mockupId);
      } else {
        newSelected.add(mockupId);
      }
      return newSelected;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedMockupIds.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedMockupIds.size} selected mockup(s)?`)) {
      return;
    }

    setLoading(true);
    try {
      // Convert Set to Array for Promise.all
      const deletePromises = Array.from(selectedMockupIds).map((id) =>
        api.delete(`/mockups/${id}`)
      );
      await Promise.all(deletePromises);

      // Filter out deleted mockups from the state
      setMockups((prevMockups) =>
        prevMockups.filter((mockup) => !selectedMockupIds.has(mockup.id))
      );
      setSelectedMockupIds(new Set()); // Clear selection
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error deleting selected mockups:', err);
      setError('Failed to delete one or more mockups.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SessionAuth>
      <div className="container mx-auto p-4 bg-background rounded-lg shadow-lg tour-mockups">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Your Mockups</h2>

        <div className="flex justify-end mb-4">
          {/* Start: Delete Button Block */}
          {selectedMockupIds.size > 0 ? (
            <button
              onClick={handleDeleteSelected}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete Selected ({selectedMockupIds.size})
            </button>
          ) : (
            <button
              disabled
              className="bg-gray-400 text-white font-bold py-2 px-4 rounded-md cursor-not-allowed"
            >
              Delete Selected
            </button>
          )}
          {/* End: Delete Button Block */}
        </div>

        {loading ? (
          <div className="text-center mt-8">Loading mockups...</div>
        ) : error ? (
          <div className="text-center mt-8 text-red-500">Error: {error}</div>
        ) : mockups.length === 0 ? (
          <p className="text-center text-gray-600">No mockups generated yet. Go to the <Link to="/design" className="text-primary hover:underline">design page</Link> to create one!</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {mockups.map((mockup) => (
              <MockupDisplayCard
                key={mockup.id}
                mockup={mockup}
                isSelected={selectedMockupIds.has(mockup.id)}
                onSelect={handleSelectMockup}
              />
            ))}
          </div>
        )}
        {!loading && !error && mockups.length > 0 && mockups.length < mockupsTotal && (
          <div className="flex justify-center mt-8">
            <button
              type="button"
              onClick={loadMoreMockups}
              disabled={loadingMore}
              className="px-6 py-3 rounded-lg bg-primary text-white font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </SessionAuth>
  );
};

export default MockupsPage;